import m from 'mithril';
import { Store } from '../store';
import { Button } from './Buttons';
import { currentLang } from '../i18n';

export const SlideOutCart: m.Component = {
    onbeforeremove(vnode) {
        // Trigger exit animations before removing from DOM
        const backdrop = vnode.dom.querySelector('.cart-backdrop');
        const drawer = vnode.dom.querySelector('.cart-drawer');
        if (backdrop) backdrop.classList.add('animate-fade-out');
        if (drawer) {
            drawer.classList.remove('animate-slide-in-right', 'animate-slide-in-left');
            drawer.classList.add(currentLang === 'ar' ? 'animate-slide-out-left' : 'animate-slide-out-right');
        }
        // Wait for animation to finish
        return new Promise(resolve => setTimeout(resolve, 300));
    },
    view() {
        if (!Store.state.isCartOpen) return null;

        const cartTotal = Store.state.cart.reduce((sum, item: any) => {
            const price = parseFloat(item.price || item.product?.price || 0);
            const qty = item.quantity || 1;
            return sum + (price * qty);
        }, 0);

        return m('div', { class: 'fixed inset-0 z-[100] grid' }, [
            // Backdrop
            m('div', { 
                class: 'cart-backdrop absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in transition-opacity',
                onclick: () => { Store.state.isCartOpen = false; m.redraw(); } 
            }),
            
            // Drawer Panel
            m('div', { 
                class: `cart-drawer absolute top-0 bottom-0 w-[400px] max-w-[100vw] bg-[var(--color-bg-base)] shadow-2xl flex flex-col 
                        end-0 border-s border-[var(--color-border)] ${currentLang === 'ar' ? 'animate-slide-in-left' : 'animate-slide-in-right'}`
            }, [
                // Header
                m('div', { class: 'flex items-center justify-between p-6 border-b border-[var(--color-border)]' }, [
                    m('h2', { class: 'text-2xl font-black tracking-tight flex items-center gap-3 text-[var(--color-text-base)]' }, [
                        m('iconify-icon', { icon: 'lucide:shopping-bag', class: 'text-[var(--color-primary)]' }),
                        'Your Cart',
                        m('span', { class: 'ms-2 px-2 py-0.5 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-sm font-bold' }, Store.state.cart.length)
                    ]),
                    m('button', {
                        class: 'w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--color-border)] transition-colors opacity-60 hover:opacity-100 text-[var(--color-text-base)] cursor-pointer border-0 bg-transparent',
                        onclick: () => { Store.state.isCartOpen = false; m.redraw(); }
                    }, m('iconify-icon', { icon: 'lucide:x', class: 'text-xl' }))
                ]),
                
                // Cart Items Area
                m('div', { class: 'flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar' }, [
                    Store.state.cart.length === 0 
                    ? m('div', { class: 'h-full flex flex-col items-center justify-center text-center opacity-70 mt-10' }, [
                        m('div', { class: 'w-24 h-24 mb-6 rounded-full bg-[var(--color-border)]/50 flex items-center justify-center' }, [
                            m('iconify-icon', { icon: 'lucide:shopping-cart', class: 'text-4xl text-[var(--color-text-base)]' })
                        ]),
                        m('h3', { class: 'text-xl font-bold text-[var(--color-text-base)] mb-2' }, 'Your cart is empty'),
                        m('p', { class: 'text-sm mb-6 max-w-[250px]' }, 'Looks like you haven\'t added any products to your cart yet.'),
                        m(Button, { 
                            variant: 'primary', 
                            onclick: () => { Store.state.isCartOpen = false; m.route.set('/shop'); } 
                        }, 'Start Shopping')
                    ])
                    : Store.state.cart.map((item: any) => {
                        const productName = item.name || item.product?.name || 'Unknown';
                        const imageSrc = item.images?.[0] || item.product?.images?.[0] || 'https://via.placeholder.com/150';
                        return m('div', { class: 'flex gap-4 p-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/30 transition-colors bg-[var(--color-bg-base)] group' }, [
                            m('div', { class: 'w-20 h-20 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] overflow-hidden shrink-0 flex items-center justify-center' }, [
                                m('img', { src: imageSrc, class: 'w-full h-full object-cover mix-blend-multiply opacity-90' })
                            ]),
                            // Info
                            m('div', { class: 'flex-1 flex flex-col justify-between py-1' }, [
                                m('div', { class: 'flex justify-between items-start gap-2' }, [
                                    m('h4', { class: 'font-bold text-sm text-[var(--color-text-base)] line-clamp-2 leading-tight' }, productName),
                                    m('button', {
                                        class: 'text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-500/10 rounded cursor-pointer border-0 bg-transparent',
                                        onclick: () => {
                                            Store.state.cart = Store.state.cart.filter((c: any) => c.id !== item.id);
                                            m.redraw();
                                        }
                                    }, m('iconify-icon', { icon: 'lucide:trash-2', class: 'text-sm' }))
                                ]),
                                m('div', { class: 'flex items-center justify-between mt-2' }, [
                                    m('div', { class: 'flex items-center bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden' }, [
                                        m('button', { 
                                            class: 'w-7 h-7 flex items-center justify-center hover:bg-[var(--color-border)] transition-colors opacity-70 hover:opacity-100 text-[var(--color-text-base)] font-bold cursor-pointer border-0 bg-transparent',
                                            onclick: () => { if ((item.quantity || 1) > 1) { item.quantity = (item.quantity || 1) - 1; m.redraw(); } }
                                        }, '-'),
                                        m('span', { class: 'w-8 text-center text-sm font-bold text-[var(--color-text-base)]' }, item.quantity || 1),
                                        m('button', { 
                                            class: 'w-7 h-7 flex items-center justify-center hover:bg-[var(--color-border)] transition-colors opacity-70 hover:opacity-100 text-[var(--color-text-base)] font-bold cursor-pointer border-0 bg-transparent',
                                            onclick: () => { item.quantity = (item.quantity || 1) + 1; m.redraw(); }
                                        }, '+')
                                    ]),
                                    m('span', { class: 'font-black text-[var(--color-primary)]' }, `$${parseFloat(item.price || item.product?.price || 0).toFixed(2)}`)
                                ])
                            ])
                        ])
                    })
                ]),

                // Cross-Sell Area (Frequently Bought Together)
                (Store.state.cart.length > 0 && Store.state.products.length > 0) ? m('div', { class: 'px-6 py-5 bg-[var(--color-surface)] border-t border-[var(--color-border)]' }, [
                    m('h3', { class: 'text-xs font-black tracking-widest uppercase opacity-60 mb-3' }, 'Suggested Add-Ons'),
                    m('div', { class: 'flex overflow-x-auto gap-3 pb-2 custom-scrollbar snap-x' }, [
                        ...Store.state.products
                            .filter((p: any) => !Store.state.cart.some((c: any) => (c.id === p.id) || (c.product?.id === p.id)))
                            .slice(0, 4)
                            .map((p: any) => 
                                m('div', { class: 'snap-center shrink-0 w-[200px] bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-xl p-3 flex flex-col gap-2 shadow-sm' }, [
                                    m('div', { class: 'flex items-center gap-3' }, [
                                        m('img', { src: p.images?.[0] || 'https://via.placeholder.com/150', class: 'w-10 h-10 rounded-lg object-cover bg-[var(--color-surface)]' }),
                                        m('div', { class: 'flex-1 min-w-0' }, [
                                            m('p', { class: 'text-xs font-bold line-clamp-1' }, p.name),
                                            m('p', { class: 'text-[10px] font-black text-[var(--color-primary)] leading-none mt-1' }, `$${Number(p.price).toFixed(2)}`)
                                        ])
                                    ]),
                                    m(Button, { 
                                        variant: 'outline', 
                                        size: 'sm', 
                                        class: 'w-full text-xs py-1.5 min-h-0 hover-spring',
                                        onclick: () => {
                                            Store.state.cart.push({ ...p, quantity: 1 });
                                            Store.addToast('Add-on added!', 'success');
                                            m.redraw();
                                        }
                                    }, m('div', { class: 'flex items-center justify-center gap-1' }, [
                                        m('iconify-icon', { icon: 'lucide:plus', class: 'text-[10px]' }), 'Add'
                                    ]))
                                ])
                            )
                    ])
                ]) : null,
                
                // Footer (Total & Checkout)
                Store.state.cart.length > 0 ? m('div', { class: 'p-6 bg-[var(--color-surface)] border-t border-[var(--color-border)] shadow-[0_-10px_40px_-20px_rgba(0,0,0,0.1)]' }, [
                    m('div', { class: 'flex justify-between items-center mb-6' }, [
                        m('span', { class: 'text-[var(--color-text-base)] opacity-70 font-bold tracking-wider text-sm uppercase' }, 'Subtotal'),
                        m('span', { class: 'text-2xl font-black text-[var(--color-primary)]' }, `$${cartTotal.toFixed(2)}`)
                    ]),
                    m('div', { class: 'flex gap-3' }, [
                        m(Button, { 
                            variant: 'outline', 
                            class: 'flex-1',
                            onclick: () => { Store.state.isCartOpen = false; m.route.set('/cart'); } 
                        }, 'View Cart'),
                        m(Button, { 
                            variant: 'primary', 
                            class: 'flex-1 shadow-[var(--shadow-premium)] hover:shadow-[0_10px_40px_-10px_var(--color-primary-glow)]',
                            onclick: () => { Store.state.isCartOpen = false; m.route.set('/checkout'); } 
                        }, 'Checkout')
                    ])
                ]) : null
            ])
        ]);
    }
};
